// src/context/SyncContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react';

interface SyncContextType {
  syncStatus: string;
  isSyncing: boolean;
  startSync: (status: string) => void;
  updateSync: (status: string) => void;
  endSync: () => void;
}

const SyncContext = createContext<SyncContextType | undefined>(undefined);

export const useSync = (): SyncContextType => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider');
  }
  return context;
};

interface SyncProviderProps {
  children: ReactNode;
}

export const SyncProvider: React.FC<SyncProviderProps> = ({ children }) => {
  const [syncStatus, setSyncStatus] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const startSync = (status: string) => {
    setSyncStatus(status);
    setIsSyncing(true);
  };

  const updateSync = (status: string) => {
    setSyncStatus(status);
  };

  const endSync = () => {
    setSyncStatus('');
    setIsSyncing(false);
  };

  //   When user closes the tab or try to refresh it should show a warning message to user that data will be lost if they continue.
  useEffect(() => {
    if (isSyncing) {
      window.onbeforeunload = () => true;
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [isSyncing]);

  return (
    <SyncContext.Provider
      value={{ syncStatus, isSyncing, startSync, updateSync, endSync }}
    >
      {children}
    </SyncContext.Provider>
  );
};
